// ═══════════════════════════════════════
// GROUPS — Gestion des groupes
// ═══════════════════════════════════════

import { currentUser, currentId, currentPartitionMeta } from './state.js';
import { authHeaders, openUserModal } from './common/auth.js';
import { navigate, closeModal, openModal } from './modal.js';
import { t } from './i18n.js';

// État local
let editingGroupId = null;
let assignGroupIds = [];
let cedeGroupId    = null;   // groupe vers l'ensemble duquel céder la partition
let assignGroups   = [];

// ═══════════════════════════════════════
// LISTE DES GROUPES
// ═══════════════════════════════════════

export async function loadGroupsList() {
    const container = document.getElementById('groups-list');
    if (!container) return;
    container.innerHTML = `<p style="color:var(--muted);font-size:13px;text-align:center">${t('groups.loading')}</p>`;
    try {
        const res    = await fetch('/api/users.php?action=groups', { headers: authHeaders() });
        const groups = await res.json();

        if (!groups.length) {
            container.innerHTML = `<p style="color:var(--muted);font-size:13px;text-align:center">${t('groups.empty')}</p>`;
            return;
        }

        container.innerHTML = groups.map(g => `
            <div class="group-item" data-id="${g.id}" data-manager="${g.is_manager ? '1' : '0'}">
                <div class="group-item-info">
                    ${g.slug
                        ? `<a class="group-item-name" href="/${window.__LANG__ !== window.__DEFAULT_LANG__ ? 'en/group/' : 'groupe/'}${g.slug}">${escHtml(g.name)}</a>`
                        : `<div class="group-item-name">${escHtml(g.name)}</div>`
                    }
                    ${g.description ? `<div class="group-item-desc">${escHtml(g.description)}</div>` : ''}
                    <div class="group-item-meta">${t('groups.member_count' + (g.member_count > 1 ? '.plural' : ''), { n: g.member_count })}</div>
                </div>
                ${g.is_delegate ? `<span class="group-role-badge">${t('groups.badge_delegate')}</span>` : ''}
                ${g.is_manager
                    ? `<button class="btn-group-edit" data-id="${g.id}" title="Modifier">✏️</button>`
                    : '<span class="group-role-badge">Membre</span>'
                }
            </div>
        `).join('');

        container.querySelectorAll('.btn-group-edit').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                openGroupEdit(parseInt(btn.dataset.id));
            });
        });
    } catch(e) {
        container.innerHTML = `<p style="color:#e74c3c;font-size:13px;text-align:center">${t('groups.error_load')}</p>`;
    }
}

// ═══════════════════════════════════════
// CRÉER / ÉDITER UN GROUPE
// ═══════════════════════════════════════

export async function openGroupCreate() {
    editingGroupId = null;
    await navigate('user-group-edit');
    const titleEl = document.getElementById('group-edit-title');
    if (titleEl) titleEl.textContent = t('groups.modal_create_title');
    _clearGroupEditForm();
    const deleteZone = document.getElementById('group-delete-zone');
    if (deleteZone) deleteZone.style.display = 'none';
}

export async function openGroupEdit(groupId) {
    editingGroupId = groupId;
    await navigate('user-group-edit');

    const titleEl = document.getElementById('group-edit-title');
    if (titleEl) titleEl.textContent = t('groups.modal_edit_title');

    const errEl      = document.getElementById('group-edit-error');
    const deleteZone = document.getElementById('group-delete-zone');
    if (errEl)      errEl.textContent        = '';
    if (deleteZone) deleteZone.style.display = '';

    try {
        const [membersRes, listRes] = await Promise.all([
            fetch(`/api/users.php?action=group-members&id=${groupId}`, { headers: authHeaders() }),
            fetch('/api/users.php?action=groups', { headers: authHeaders() })
        ]);
        const data   = await membersRes.json();
        const groups = await listRes.json();
        const group  = groups.find(g => g.id === groupId);

        const nameEl  = document.getElementById('group-name');
        const descEl  = document.getElementById('group-description');
        const emailEl = document.getElementById('group-emails');
        if (nameEl)  nameEl.value  = group?.name        ?? '';
        if (descEl)  descEl.value  = group?.description ?? '';
        if (emailEl) emailEl.value = data.emails.join('\n');

        // Un penn pupitre gère le groupe mais ne peut pas le supprimer
        if (deleteZone) deleteZone.style.display = group?.is_owner ? '' : 'none';

        await _renderDelegates(groupId);
    } catch(e) {
        if (errEl) errEl.textContent = t('groups.error_load');
    }
}

// ═══════════════════════════════════════
// PENN PUPITRES (délégation d'un groupe Ensemble)
// ═══════════════════════════════════════

// Bloc inséré dans la modale d'édition, juste avant le message d'erreur :
// la liste des penn pupitres, et pour le propriétaire l'ajout et le retrait.
async function _renderDelegates(groupId) {
    const errEl = document.getElementById('group-edit-error');
    if (!errEl) return;

    let zone = document.getElementById('group-delegates');
    if (!zone) {
        zone = document.createElement('div');
        zone.id = 'group-delegates';
        zone.className = 'group-delegates';
        errEl.parentNode.insertBefore(zone, errEl);
    }

    const res  = await fetch(`/api/users.php?action=group-delegates&id=${groupId}`, { headers: authHeaders() });
    if (!res.ok) { zone.innerHTML = ''; return; }
    const data = await res.json();

    const myId = parseInt(currentUser?.id ?? 0, 10);
    const rows = data.delegates.map(d => {
        const isMe   = d.id === myId;
        const action = data.can_edit
            ? `<a href="#" class="group-delegate-remove" data-uid="${d.id}" data-name="${escHtml(d.username)}">${t('groups.delegate_remove')}</a>`
            : (isMe ? `<a href="#" class="group-delegate-remove" data-uid="${d.id}" data-self="1">${t('groups.delegate_leave')}</a>` : '');
        return `
            <div class="group-delegate-row">
                <span class="group-delegate-name">${escHtml(d.username)} <span class="group-delegate-email">${escHtml(d.email)}</span></span>
                ${action}
            </div>`;
    }).join('');

    zone.innerHTML = `
        <label class="group-delegates-title">
            ${escHtml(t('groups.delegates_title', { n: data.used, max: data.max }))}
        </label>
        <div class="group-delegates-list">
            ${rows || `<p class="group-delegates-none">${t('groups.delegates_none')}</p>`}
        </div>
        ${data.can_edit ? `
        <div class="group-delegates-add">
            <input type="email" id="group-delegate-email" placeholder="${escHtml(t('groups.delegate_placeholder'))}">
            <button type="button" class="btn-confirm" id="group-delegate-add">${t('groups.delegate_add')}</button>
        </div>
        <p class="group-delegates-hint">${t('groups.delegates_hint')}</p>` : ''}
    `;

    document.getElementById('group-delegate-add')?.addEventListener('click', async () => {
        const email = document.getElementById('group-delegate-email')?.value.trim() ?? '';
        errEl.textContent = '';
        if (!email) return;
        try {
            const r = await fetch(`/api/users.php?action=group-delegate-add&id=${groupId}`, {
                method: 'POST', headers: authHeaders(), body: JSON.stringify({ email })
            });
            const d = await r.json();
            if (!r.ok) { errEl.textContent = d.error || 'Erreur'; return; }
            await _renderDelegates(groupId);
        } catch(e) { errEl.textContent = t('groups.error_network'); }
    });

    zone.querySelectorAll('.group-delegate-remove').forEach(link => {
        link.addEventListener('click', async (e) => {
            e.preventDefault();
            const self = link.dataset.self === '1';
            const msg  = self
                ? t('groups.delegate_leave_confirm')
                : t('groups.delegate_remove_confirm', { name: link.dataset.name });
            if (!confirm(msg)) return;
            errEl.textContent = '';
            try {
                const r = await fetch(`/api/users.php?action=group-delegate&id=${groupId}&uid=${link.dataset.uid}`, {
                    method: 'DELETE', headers: authHeaders()
                });
                const d = await r.json();
                if (!r.ok) { errEl.textContent = d.error || 'Erreur'; return; }
                if (self) { await navigate('user-groups'); loadGroupsList(); return; }
                await _renderDelegates(groupId);
            } catch(e) { errEl.textContent = t('groups.error_network'); }
        });
    });
}

export async function submitGroupSave() {
    const name        = document.getElementById('group-name')?.value.trim()        ?? '';
    const description = document.getElementById('group-description')?.value.trim() ?? '';
    const emails      = document.getElementById('group-emails')?.value              ?? '';
    const errEl       = document.getElementById('group-edit-error');
    if (errEl) errEl.textContent = '';

    if (!name) { if (errEl) errEl.textContent = t('groups.error_name_required'); return; }

    const body = { name, description, emails };
    const url  = editingGroupId
        ? `/api/users.php?action=group-update&id=${editingGroupId}`
        : '/api/users.php?action=group-create';

    try {
        const res  = await fetch(url, { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
        const data = await res.json();
        if (res.ok) {
            await navigate('user-groups');
            loadGroupsList();
        } else {
            if (errEl) errEl.textContent = data.error || 'Erreur';
        }
    } catch(e) { if (errEl) errEl.textContent = t('groups.error_network'); }
}

export async function submitGroupDelete() {
    if (!editingGroupId) return;
    if (!confirm(t('groups.delete_confirm'))) return;

    try {
        const res  = await fetch(`/api/users.php?action=group&id=${editingGroupId}`, {
            method: 'DELETE', headers: authHeaders()
        });
        const data = await res.json();
        if (res.ok) {
            await navigate('user-groups');
            loadGroupsList();
        } else {
            const errEl = document.getElementById('group-edit-error');
            if (errEl) errEl.textContent = data.error || 'Erreur';
        }
    } catch(e) {
        const errEl = document.getElementById('group-edit-error');
        if (errEl) errEl.textContent = t('groups.error_network');
    }
}

// ═══════════════════════════════════════
// ASSIGNER UNE PARTITION À DES GROUPES
// ═══════════════════════════════════════

export async function openGroupAssign() {
    if (!currentId) return;
    if (!currentUser) { openUserModal(); return; }

    await openModal('user-group-assign', async (nav, controller) => {
        const { signal } = controller;
        const errEl      = document.getElementById('group-assign-error');
        if (errEl) errEl.textContent = '';

        const titleEl      = document.getElementById('group-assign-partition-name');
        const displayTitle = document.getElementById('partition-title-display')?.textContent || '';
        if (titleEl) titleEl.textContent = `« ${displayTitle} »`;

        document.querySelector('#modal-content .btn-cancel')
            ?.addEventListener('click', () => closeModal(), { signal });
        document.querySelector('#modal-content .btn-confirm')
            ?.addEventListener('click', () => submitGroupAssign(), { signal });

        try {
            const [groupsRes, assignedRes] = await Promise.all([
                fetch('/api/users.php?action=groups', { headers: authHeaders() }),
                fetch(`/api/users.php?action=partition-groups&partition_id=${currentId}`, { headers: authHeaders() })
            ]);
            const groups   = await groupsRes.json();
            const assigned = await assignedRes.json();

            const listEl = document.getElementById('group-assign-list');
            if (!listEl) return;

            if (!groups.length) {
                listEl.innerHTML = `<p style="color:var(--muted);font-size:13px">${t('groups.no_groups_msg')}</p>`;
                assignGroupIds = [];
                return;
            }

            const assignedIds = new Set(assigned.map(g => g.id));
            assignGroupIds = [...assignedIds];
            assignGroups   = groups;
            cedeGroupId    = null;

            // La cession n'est proposée qu'au propriétaire de la partition,
            // pour les groupes dont il est penn pupitre.
            const owns = currentPartitionMeta?.owns ?? currentPartitionMeta?.is_owner ?? false;

            listEl.innerHTML = groups.map(g => `
                <label class="group-assign-item">
                    <input type="checkbox" class="group-assign-chk" value="${g.id}" ${assignedIds.has(g.id) ? 'checked' : ''}>
                    <span class="group-assign-name">${escHtml(g.name)}</span>
                    ${g.description ? `<span class="group-assign-desc">${escHtml(g.description)}</span>` : ''}
                </label>
                ${owns && g.is_delegate ? `
                <label class="group-assign-item group-assign-cede">
                    <input type="checkbox" class="group-cede-chk" value="${g.id}">
                    <span>${t('groups.cede_label')}</span>
                </label>` : ''}
            `).join('');

            listEl.querySelectorAll('.group-cede-chk').forEach(chk => {
                chk.addEventListener('change', () => {
                    const id = parseInt(chk.value);
                    // Une seule cession possible : décocher les autres
                    listEl.querySelectorAll('.group-cede-chk').forEach(o => { if (o !== chk) o.checked = false; });
                    cedeGroupId = chk.checked ? id : null;
                    // Céder range aussi la partition dans le groupe
                    if (chk.checked) {
                        const assignChk = listEl.querySelector(`.group-assign-chk[value="${id}"]`);
                        if (assignChk && !assignChk.checked) {
                            assignChk.checked = true;
                            if (!assignGroupIds.includes(id)) assignGroupIds.push(id);
                        }
                    }
                }, { signal });
            });

            listEl.querySelectorAll('.group-assign-chk').forEach(chk => {
                chk.addEventListener('change', () => {
                    const id = parseInt(chk.value);
                    if (chk.checked) {
                        if (!assignGroupIds.includes(id)) assignGroupIds.push(id);
                    } else {
                        assignGroupIds = assignGroupIds.filter(i => i !== id);
                    }
                }, { signal });
            });

        } catch(e) {
            const listEl = document.getElementById('group-assign-list');
            if (listEl) listEl.innerHTML = `<p style="color:#e74c3c;font-size:13px">${t('groups.error_load')}</p>`;
            assignGroupIds = [];
        }
    });
}

export async function submitGroupAssign() {
    if (!currentId) return;
    const errEl = document.getElementById('group-assign-error');
    if (errEl) errEl.textContent = '';

    // Cession demandée : confirmation explicite avant toute écriture
    let cedeGroup = null;
    if (cedeGroupId) {
        cedeGroup = assignGroups.find(g => g.id === cedeGroupId) || null;
        const title = document.getElementById('partition-title-display')?.textContent || '';
        if (!confirm(t('groups.cede_confirm', { title, group: cedeGroup?.name ?? '' }))) return;
        if (!assignGroupIds.includes(cedeGroupId)) assignGroupIds.push(cedeGroupId);
    }

    try {
        const res  = await fetch('/api/users.php?action=partition-assign', {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ partition_id: currentId, group_ids: assignGroupIds })
        });
        const data = await res.json();
        if (!res.ok) {
            if (errEl) errEl.textContent = data.error || 'Erreur';
            return;
        }

        if (cedeGroup) {
            const r2 = await fetch('/api/users.php?action=partition-cede', {
                method: 'POST',
                headers: authHeaders(),
                body: JSON.stringify({ partition_id: currentId, group_id: cedeGroup.id })
            });
            const d2 = await r2.json();
            if (!r2.ok) {
                if (errEl) errEl.textContent = d2.error || 'Erreur';
                return;
            }
            // Les droits ont changé (plus propriétaire) : recharger la page
            window.location.reload();
            return;
        }

        closeModal();
    } catch(e) { if (errEl) errEl.textContent = t('groups.error_network'); }
}

// ═══════════════════════════════════════
// UTILITAIRES
// ═══════════════════════════════════════

function _clearGroupEditForm() {
    const fields = ['group-name', 'group-description', 'group-emails', 'group-edit-error'];
    fields.forEach(id => {
        const el = document.getElementById(id);
        if (el) el.value !== undefined ? (el.value = '') : (el.textContent = '');
    });
    // Création : pas encore de penn pupitres
    document.getElementById('group-delegates')?.remove();
}

function escHtml(str) {
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}